'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import GoogleMap from '@components/Widgets/GoogleMap.react';
import SearchKeywords from '@components/Search/Filters/SearchKeywords.react';
import ContactFormModal from '@components/Widgets/ContactFormModal.react';

import AuthStore from '@stores/AuthStore'
import UserStore from '@stores/UserStore';
import UserActions from '@actions/UserActions';

import Analytics from '@utils/Analytics';
import { getConfig } from '@utils/Env';
import { roundForHumans, roundForHumansByUnit, getKmBetweenCoordinates } from '@utils/Math';
import { computeDefaultMealRx, transformEnvelopeForRestaurants, convertEnvelopeToFilters }  from '@utils/Nutrition';

import './Locations.scss';

export default class RestaurantLocations extends Component {

    static propTypes = {
        onSelectLocation: PropTypes.func,
        savedLocationParams: PropTypes.object,
    };

    static contextTypes = {
        addSwapContext: PropTypes.object,
        showCreateCustom: PropTypes.func,
    };

    constructor(props, context) {
        super(props, context);

        // Use saved location params if available, otherwise use defaults
        const defaultParams = this.getDefaultLocationParams(context.addSwapContext);
        const locationParams = props.savedLocationParams || defaultParams;

        this.state = {
            locationParams,
            locations: [],
            totalLocations: 0,
            showSearchHere: false,
            showAutocomplete: false,
            loading: true,
            searchExpansionLevel: 0,
            allowExpansion: true,
        };

        this.debounceSearchLocations = debounce(this.searchLocations, 400);
    }

    map = null;
    locationSearchKey = null;

    realizeMap = (map) => {
        this.map = map;
    }

    /**
     * Converts the parameters we get from the parent AddSwapRecipe modal to a restaurant search parameters
     * @param  {[type]} inputs [description]
     * @return {[type]}        [description]
     */
    getDefaultLocationParams = ({profile, mealType}) => {
        let mealTypeTag = mealType;

        if (mealTypeTag === 'Dinner') {
            mealTypeTag = 'Main Dish';
        }

        const params = {
            types: ['brand_location'],
            terms: '',
            filters: {
                matching_products: {
                    language: profile.language || 'en',
                    '!ingredient_tags': profile?.preferences?.avoidances,
                },
            },
        };

        const allDayRx = profile.prescriptions.filter((p) => p.meal_type === 'all-day')[0];
        const rx =
            profile.prescriptions.filter((p) => p.meal_type === mealType)[0] ||
            (allDayRx && computeDefaultMealRx(allDayRx, mealType, true));

        const okChoice = rx && allDayRx ? transformEnvelopeForRestaurants(rx.envelope, allDayRx.envelope, true) : null;

        if (okChoice) {
            params.filters.matching_products = convertEnvelopeToFilters(okChoice, params.filters.matching_products);
        }

        return params;
    }

    computeDistanceToLocationResults = (locations) => {
        if (!this.map) {
            return locations;
        }

        const currentLocation = this.map.currentLocation;

        locations.forEach(location => {
            location.distance_km = getKmBetweenCoordinates(
                currentLocation.lat, currentLocation.lon,
                location.location.lat, location.location.lon
            );
        });

        return locations;
    }

    searchLocations = async () => {
        // Deep clone parameters
        const locationSearchKey = JSON.stringify(this.state.locationParams);

        if (locationSearchKey === this.locationSearchKey) {
            return;
        }

        this.locationSearchKey = locationSearchKey;
        const params = JSON.parse(locationSearchKey);
        params.size = 250;

        this.setState({loading: true});

        Analytics.searchRestaurantLocations();

        let results;

        try {
            results = await AuthStore.fetch(getConfig('recipe_api') + '/search', {
                method: 'POST',
                headers: {'Content-Type': 'application/json; schema=search/advanced/1'},
                body: JSON.stringify(params),
            }, true);
        } catch (error) {
            this.setState({loading: false, error: error.message});
            return;
        }

        // If we got zero results and expansion is allowed, check if we can expand further (up to ~250 miles)
        if (results.elements.length === 0 && this.state.allowExpansion) {
            // Calculate current search radius to see if we can expand more
            const bounds = this.map.getBounds();
            if (bounds) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                const { searchExpansionLevel } = this.state;
                const expansionMultiplier = 1 + ((searchExpansionLevel + 1) * 0.5); // Next level's multiplier
                
                const latBuffer = (ne.lat() - sw.lat()) * (expansionMultiplier - 1) * 0.5;
                const lngBuffer = (ne.lng() - sw.lng()) * (expansionMultiplier - 1) * 0.5;
                
                const centerLat = (ne.lat() + sw.lat()) / 2;
                const centerLng = (ne.lng() + sw.lng()) / 2;
                const expandedNeLat = ne.lat() + latBuffer;
                const expandedNeLng = ne.lng() + lngBuffer;
                const nextRadiusKm = getKmBetweenCoordinates(centerLat, centerLng, expandedNeLat, expandedNeLng);
                const nextRadiusMiles = nextRadiusKm * 0.621371;
                
                // Only expand if next radius would be under 250 miles
                if (nextRadiusMiles <= 250) {
                    this.setState({searchExpansionLevel: this.state.searchExpansionLevel + 1}, () => {
                        this.performSearchWithBounds();
                    });
                    return;
                }
            }
        }

        const locations = this.computeDistanceToLocationResults(results.elements);

        // Always sort by distance, regardless of search terms
        locations.sort((a, b) => a.distance_km - b.distance_km);

        // Is the map rendered? Clear, then add the markers
        if (this.map) {
            this.map.deleteMarkers();

            // Create a marker for my current location using the blue marker pin
            this.map.createCurrentLocationMarker();

            locations.forEach(establishment => {
                establishment.marker = this.map.addMarker(
                    {lat: establishment.location.lat, lng: establishment.location.lon},
                    establishment.title,
                    this.renderEstablishmentInfo(establishment),
                );
            });
        }

        this.setState({
            locations,
            totalLocations: results.total,
            loading: false,
            showSearchHere: false,
        });
    }

    onMapInitialized = () => {
        this.performSearchWithBounds();
    }

    onFocusSearch = () => {
        this.setState({showAutocomplete: true});
    }

    onBlurSearch = () => {
        this.setState({showAutocomplete: false});
    }

    onChangeLocationParams = (locationParams) => {
        this.setState({locationParams, searchExpansionLevel: 0, allowExpansion: true}, this.debounceSearchLocations);
    }

    showCreateCustom = () => {
        const { showCreateCustom } = this.context;

        showCreateCustom({
            defaultProductType: 'Restaurant Dish',
            defaultCategory: 'Restaurant Meal',
        });
    };

    performSearchWithBounds = (overrideAllowExpansion = null) => {
        if (!this.map) {
            return;
        }

        const bounds = this.map.getBounds();

        if (!bounds) {
            return;
        }

        const { locationParams } = this.state;

        locationParams.filters = locationParams.filters || {};
        locationParams.filters.geo_bounds = locationParams.filters.geo_bounds || {};

        // Transform top/right & bottom/left <= to => top/left & bottom/right
        const ne = bounds.getNorthEast(),
              sw = bounds.getSouthWest();

        // Apply expansion based on current expansion level
        const { searchExpansionLevel, allowExpansion } = this.state;
        const effectiveAllowExpansion = overrideAllowExpansion !== null ? overrideAllowExpansion : allowExpansion;
        
        // Calculate current radius to determine expansion strategy
        const centerLat = (ne.lat() + sw.lat()) / 2;
        const centerLng = (ne.lng() + sw.lng()) / 2;
        const currentRadiusKm = getKmBetweenCoordinates(centerLat, centerLng, ne.lat(), ne.lng());
        const currentRadiusMiles = currentRadiusKm * 0.621371;
        
        let expansionMultiplier;
        if (effectiveAllowExpansion && searchExpansionLevel > 0) {
            if (currentRadiusMiles < 10) {
                // For very small radii, jump to at least 15 miles to avoid tiny increments
                expansionMultiplier = Math.max(15 / currentRadiusMiles, 1 + (searchExpansionLevel * 0.5));
            } else {
                // Normal 50% expansion for reasonable starting radii
                expansionMultiplier = 1 + (searchExpansionLevel * 0.5);
            }
        } else {
            // No expansion - use original bounds
            expansionMultiplier = 1;
        }
        
        const latBuffer = (ne.lat() - sw.lat()) * (expansionMultiplier - 1) * 0.5;
        const lngBuffer = (ne.lng() - sw.lng()) * (expansionMultiplier - 1) * 0.5;

        // Calculate approximate radius for logging
        const expandedNeLat = ne.lat() + latBuffer;
        const expandedNeLng = ne.lng() + lngBuffer;
        const expandedRadiusKm = getKmBetweenCoordinates(centerLat, centerLng, expandedNeLat, expandedNeLng);
        const expandedRadiusMiles = expandedRadiusKm * 0.621371;

        locationParams.filters.geo_bounds.top = ne.lat() + latBuffer;
        locationParams.filters.geo_bounds.left = sw.lng() - lngBuffer;
        locationParams.filters.geo_bounds.bottom = sw.lat() - latBuffer;
        locationParams.filters.geo_bounds.right = ne.lng() + lngBuffer;

        // Add geo-distance sorting when there are no search terms
        if (!locationParams.terms || locationParams.terms.trim() === '') {
            locationParams.sort_by = 'geo_distance';
            locationParams.sort_params = {
                lat: centerLat,
                lon: centerLng
            };
        }

        // Update map bounds to match the expanded search area
        if (expansionMultiplier > 1 && this.map) {
            const expandedBounds = new window.google.maps.LatLngBounds(
                new window.google.maps.LatLng(sw.lat() - latBuffer, sw.lng() - lngBuffer),
                new window.google.maps.LatLng(ne.lat() + latBuffer, ne.lng() + lngBuffer)
            );
            this.map.fitBounds(expandedBounds);
        }

        this.setState({locationParams}, this.searchLocations);
    }

    onSearchHere = () => {
        this.setState({allowExpansion: false}, () => {
            this.performSearchWithBounds(false);
        });
    }

    onPlaceChanged = () => {
        this.setState({searchExpansionLevel: 0, allowExpansion: true}, () => {
            this.performSearchWithBounds();
        });
    }

    onBoundsChanged = (bounds) => {
        this.setState({showSearchHere: true});
    }

    onSelectLocation = (location) => {
        const { onSelectLocation } = this.props;
        const { locationParams } = this.state;

        onSelectLocation(location, locationParams);
    }

    onLocationFound = (currentLocation) => {
        this.onSearchHere();

        // Courtesy update of the user store's location, so we have this exact location available for later.
        // when the map isn't being rendered.
        UserActions.updateCurrentLocation(currentLocation.lat, currentLocation.lon);

        Analytics.userLocated({
            'Context': 'Restaurant Search',
            'Latitude': currentLocation.lat,
            'Longitude': currentLocation.lon,
        });
    }

    renderEstablishmentInfo = (location) => {
        const { title, address1, address2, distance_km, yelp_price, matching_products } = location;
        const { units_mode = 'english' } = UserStore.getUser() || {};

        return (
            <div className="establishment-info" onClick={() => this.onSelectLocation(location)}>
                {matching_products ?
                     <p className="matching">{matching_products} {matching_products === 1 ? 'dish' : 'dishes'}</p>
                : null}
                <h3>{title}</h3>
                <p>
                    {units_mode === 'metric' ? <span>{roundForHumans(distance_km)}km - {address1}</span> : null}
                    {units_mode === 'english' ? <span>{roundForHumans(distance_km * 0.621371)}mi - {address1}</span> : null}
                    <span className="yelp-price"> - <em>{('$').repeat(yelp_price)}</em></span>
                </p>
            </div>
        );
    }

    renderLocationResult = (location, i) => {
        const { units_mode = 'english' } = UserStore.getUser() || {};

        return (
            <div key={i} className="location-result-item" onClick={() => this.onSelectLocation(location)}>
                {location.matching_products ?
                    <span className="matching">{location.matching_products} {location.matching_products === 1 ? 'recommendation' : 'recommendations'}</span>
                : null}

                <h3>{location.title}</h3>
                <p>
                    {units_mode === 'metric'
                        ? <span>{roundForHumans(location.distance_km)}km</span>
                        : <span>{roundForHumans(location.distance_km * 0.621371)}mi</span>
                    }
                    <span>- {location.address1}</span>
                    <span className="yelp-price"> - <em>{('$').repeat(location.yelp_price)}</em>{('$').repeat(4-location.yelp_price)}</span>
                </p>
            </div>
        );
    }

    render = () => {
        const { locationParams, showSearchHere, showAutocomplete, locations, loading } = this.state;

        return (
            <div className="restaurant-locations">
                <div className="outer-map-container">
                    <SearchKeywords params={locationParams}
                        onFocus={this.onFocusSearch}
                        onBlur={this.onBlurSearch}
                        className="map-keywords-filter"
                        onChangeParams={this.onChangeLocationParams}
                        placeholder="Search Restaurants" />

                    <GoogleMap ref={this.realizeMap}
                        onBoundsChanged={this.onBoundsChanged}
                        onPlaceChanged={this.onPlaceChanged}
                        onMapInitialized={this.onMapInitialized}
                        onLocationFound={this.onLocationFound}
                        onSearchHere={this.onSearchHere}
                        showSearchHere={showSearchHere}
                        showAutocomplete={true} />
                </div>

                <div className="outer-results-container">
                    <div className="inner-results-container">
                        <ContactFormModal defaultSubject="report-issue" sourceLabel="Restaurant Search - Report Issue" className="report-issue-btn">Report Issue</ContactFormModal>

                        {loading ?
                            <div className="loading">
                                <h2>Loading...</h2>
                                <i className="icon-spinner" />
                            </div>
                        : null}

                        {locations.map(this.renderLocationResult)}

                        {!loading && locations.length == 0 && locationParams.terms ?
                            <div className="no-results-found">
                                <h2>Sorry, we couldn't find {locationParams.terms}</h2>
                                <p>We don’t currently have this store’s meal information. If you would like to still eat foods from this store, tell us what you will be eating there so we can track nutrition information.</p>

                                <footer>
                                    <button className="el-medium-btn el-raspberry-btn create-meal-btn" onClick={this.showCreateCustom}>Create a Meal</button>
                                </footer>
                            </div>
                        : null}

                        {!loading && locations.length == 0 && !locationParams.terms ?
                            <div className="no-results-found">
                                <h2>Sorry, we couldn't find anything in this area</h2>
                                <p>Thanks for your patience. We are working on adding new restaurants every day.</p>
                                <p>In the meantime, create a custom meal for your favorite local restaurant. This will allow you to track this meal in your planner and log.</p>
                                <footer>
                                    <button className="el-medium-btn el-raspberry-btn create-meal-btn" onClick={this.showCreateCustom}>Create a Custom Meal</button>
                                </footer>
                            </div>

                        : null}

                    </div>
                </div>
            </div>
        );
    }
}
